import CustomField from '../../../../../../../../elements/form/CustomField'
import Button from '../../../../../../../../elements/Button'
import { Formik, Form } from 'formik'
import { useState, useEffect } from 'react'
import Radio from '../../../../../../../../elements/form/Radio'
import { useModalContext } from '../../../../../../../../../providers/modalProvider'
import axios from '/components/configs/axios'
import ThankYou from './ThankYou'
import StripeComponent from '/components/pages/single-property/StripeComponent'
import styles from './ThankYou/styles.module.scss'
import Column from '../../../../../../../../layout/Column'
import Columns from '../../../../../../../../layout/Columns'
import Details from './Details'
import IconContact from '/public/icons/icon-step-contact.svg'
import IconPayment from '/public/icons/icon-step-payment.svg'
import { useResponsive } from '../../../../../../../../../providers/ResponsiveContext'
import * as Yup from 'yup'
import { useRouter } from 'next/router'
import { useSearchContext } from '../../../../../../../../../providers/SearchContext'

const ModalContent = ({
  propertyId,
  guests,
  total,
  bedrooms,
  property,
  taxesAndFess,
  totalCostPerNights,
  totalWithoutStripe,
  bonuses,
  numberOfFreeNights,
  freeNightsCost,
  freeNightsOldPrice,
  translations
}) => {
  const router = useRouter()
  const { search } = useSearchContext()
  const startDate = search.startDate
  const endDate = search.endDate
  const ReservationSchema = Yup.object().shape({
    firstName: Yup.string()
      .max(25)
      .matches(
        /^'?\p{L}+(?:[' ]\p{L}+)*'?$/u,
        translations.form.error.validName[router.locale]
      )
      .required(translations.form.error.requiredFirstName[router.locale]),
    lastName: Yup.string()
      .max(25)
      .matches(
        /^'?\p{L}+(?:[' ]\p{L}+)*'?$/u,
        translations.form.error.validName[router.locale]
      )
      .required(translations.form.error.requiredLastName[router.locale]),
    email: Yup.string()
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )
      .max(254)
      .email(translations.form.error.validEmail[router.locale])
      .required(translations.form.error.requiredEmail[router.locale])
  })

  const { handleCloseModal } = useModalContext()
  const { isMobile } = useResponsive()

  const [plans, setPlans] = useState('details')
  const [skip, setSkip] = useState(false)
  const [coupon, setCoupon] = useState(false)
  const [updatedPrice, setUpdatedPrice] = useState(false)
  const [reservationId, setReservationId] = useState('')
  const [agreement, setAgreement] = useState(false)
  const isThankYou = plans === 'thankYou'

  const detailsProps = {
    guests,
    total,
    startDate,
    endDate,
    bedrooms,
    property,
    updatedPrice,
    setUpdatedPrice,
    setCoupon,
    taxesAndFess,
    totalCostPerNights,
    totalWithoutStripe,
    bonuses,
    numberOfFreeNights,
    freeNightsCost,
    freeNightsOldPrice,
    translations
  }

  const formInitialValues = {
    id: propertyId,
    startDate: startDate,
    endDate: endDate,
    bedrooms: bedrooms,
    total: Number(total),
    guests: guests,
    bonuses: bonuses,
    propertyName: property?.propertyTitle,
    firstName: '',
    lastName: '',
    email: '',
    agreement: false,
    phone: null,
    car: null,
    transport: null,
    assistance: null,
    food: null,
    reservation: null
  }

  const [submitted, setSubmitted] = useState(formInitialValues)

  const handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(true)
    if (plans === 'details') {
      axios
        .post('reservation/payment-intent', {
          params: values
        })
        .catch((err) => console.error(err))
      //stripe verification if previous is valid
      //if everything checks, set Plans (thank you form)
      setPlans('payment')
      setSubmitted({
        ...submitted,
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName
      })
    }

    if (plans === 'thankYou') {
      axios
        .post('reservation/questions', {
          ...values,
          coupon,
          bonuses,
          total,
          freeNights: numberOfFreeNights,
          freeNightlyRate: freeNightsCost,
          initialPrice: freeNightsOldPrice,
          reservationId: reservationId
        })
        .then(() => {
          setPlans('finish')
        })
        .catch((err) => console.error(err))
    }
    setSubmitted(...formInitialValues, values)
    setSubmitting(false)
  }

  if (plans !== 'finish') {
    return (
      <Columns variant={[isThankYou && 'no-gutter']}>
        <Column
          variant={[isThankYou && 'no-padding']}
          spans={isThankYou || isMobile ? 12 : 7}
        >
          <Formik
            onSubmit={handleSubmit}
            enableReinitialize
            validationSchema={ReservationSchema}
            initialValues={formInitialValues}
          >
            {({ values, setFieldValue, errors, touched }) => {
              useEffect(() => {
                setFieldValue('agreement', agreement)
              }, [agreement])

              useEffect(() => {
                setFieldValue('total', updatedPrice.finalCost)
              }, [updatedPrice])

              if (plans === 'details') {
                return (
                  <Form className={styles.form}>
                    <IconContact />

                    <h2 className={styles.form__title}>
                      {translations.contactDetails[router.locale]}
                    </h2>

                    <div>
                      <CustomField
                        variant={['hidden']}
                        readOnly
                        name="id"
                        value={propertyId}
                      />

                      <CustomField
                        variant={['hidden']}
                        readOnly
                        name="startDate"
                        value={startDate}
                      />

                      <CustomField
                        variant={['hidden']}
                        readOnly
                        name="endDate"
                        value={endDate}
                      />

                      <CustomField
                        variant={['hidden']}
                        readOnly
                        name="bedrooms"
                        value={bedrooms}
                      />

                      <CustomField
                        variant={['hidden']}
                        readOnly
                        name="total"
                        value={Number(total)}
                      />

                      <CustomField
                        variant={['full-width']}
                        required
                        maxLength={20}
                        name="firstName"
                        placeholder={
                          translations.form.fields.firstName[router.locale]
                        }
                        className={styles.form__inputs}
                        onChange={(e) => {
                          setFieldValue(
                            'firstName',
                            e.target.value.replace(/[0-9]/g, '')
                          )
                        }}
                        error={errors.firstName && touched?.firstName}
                      />

                      <CustomField
                        variant={['full-width']}
                        maxLength={20}
                        required
                        name={translations.form.fields.lastName[router.locale]}
                        placeholder={
                          translations.form.fields.lastName[router.locale]
                        }
                        className={styles.form__inputs}
                        onChange={(e) => {
                          setFieldValue(
                            'lastName',
                            e.target.value.replace(/[0-9]/g, '')
                          )
                        }}
                        error={errors.lastName && touched?.lastName}
                      />

                      <CustomField
                        variant={['full-width']}
                        maxLength={254}
                        name="email"
                        required
                        placeholder={
                          translations.form.fields.email[router.locale]
                        }
                        type="email"
                        className={styles.form__inputs}
                        error={errors.email && touched.email}
                        valid={values.email && !errors.email && touched.email}
                      />

                      <Radio
                        variant={['agreement']}
                        name="agreement"
                        checkbox
                        checked={agreement}
                        onChange={() => setAgreement(!agreement)}
                        label={translations.form.agreement[router.locale]}
                      />
                    </div>

                    <Button
                      disabled={
                        values.email === null ||
                        values.firstName === null ||
                        values.lastName === null
                      }
                    >
                      {translations.form.fields.continue[router.locale]}
                    </Button>
                  </Form>
                )
              }
              if (plans === 'payment') {
                return (
                  <div className={styles.form}>
                    <IconPayment />

                    <h2 className={styles.form__title}>
                      {translations.stripe.paymentDetails[router.locale]}
                    </h2>

                    <StripeComponent
                      translations={translations.stripe}
                      setReservationId={setReservationId}
                      setPlans={setPlans}
                      setSkip={setSkip}
                      updatedPrice={updatedPrice}
                      coupon={coupon}
                      freeNights={numberOfFreeNights}
                      freeNightlyRate={freeNightsCost}
                      initialPrice={freeNightsOldPrice}
                      {...submitted}
                      propertyId={detailsProps.property?.resource_id}
                      listingId={detailsProps.property?.id}
                      total={
                        updatedPrice
                          ? Number(updatedPrice.finalCost)
                          : Number(total)
                      }
                    />
                  </div>
                )
              }

              if (isThankYou) {
                return (
                  <ThankYou
                    translations={translations.thankYou}
                    setFieldValue={setFieldValue}
                  />
                )
              }
            }}
          </Formik>
        </Column>

        {!isThankYou && (
          <Column spans={isMobile ? 12 : 5}>
            <Details {...detailsProps} />
          </Column>
        )}
      </Columns>
    )
  }

  return (
    <div className={styles.finish}>
      <h2 className={styles.finish__title}>
        {skip
          ? translations.skip.title1[router.locale]
          : translations.skip.title2[router.locale]}
      </h2>
      <span className={styles.finish__subtitle}>
        {skip
          ? translations.skip.subtitle1[router.locale]
          : translations.skip.subtitle2[router.locale]}
      </span>
      <div className={styles.finish__separator}></div>
      <p className={styles.finish__description}>
        {translations?.skip.questions[router.locale]}
        <a rel="nofollow" href="mailto:reservations@stmartinsothebysrealty.com">
          reservations@stmartinsothebysrealty.com
        </a>
      </p>

      <Button
        href="/guest-experience"
        onClick={() => {
          handleCloseModal()
        }}
        arrow={false}
        variant={['color-accent']}
      >
        {translations.skip.button[router.locale]}
      </Button>
    </div>
  )
}

export default ModalContent
